import { isCvinExpansion } from "../projectGridColumns";
import { displayPhaseForRow, EXPANSION_PHASE_FLOW, GLOBAL_PHASES } from "../projectPhaseDisplay";
import { buildExpansionSegmentDetailRow } from "./expansionSegmentDetailExport";
import {
  buildExpansionSegmentGroups,
  formatExpansionFootage,
  isExpansionParentShellRow,
  parseFootageNumber,
} from "./expansionGroupUtils";
import { firstInspectorUnitsReportDate, sumInspectorReportedFootage } from "./expansionProgressUtils";
import { readSubsegmentSummaryFromAcf } from "./expansionProjectAcf";

export const TOTAL_PERMITTED_METRIC_KEY = "Total Permitted";
export const NEW_FEET_METRIC_KEY = "New Feet";
/** Inspector conduit footage — labeled "Completed Construction Footage" in the reporting dropdown. */
export const COMPLETED_CONDUIT_METRIC_KEY = "Completed Construction Footage";
export const COMPLETED_FIBER_METRIC_KEY = "Completed Fiber Footage";
export const COMPLETED_PROOFING_METRIC_KEY = "Completed Proofing Footage";
/** Budgeted value of awarded footage (same formula as Awarded Budget Value, with a derived $/ft fallback). */
export const BUDGETED_METRIC_KEY = "Budgeted";

const CALTRANS_PIE_COLORS = {
  caltrans: "#2563eb",
  nonCaltrans: "#16a34a",
};

/** Same rule as permitted reporting: "Caltrans" in the project name. */
export function isCaltransCompletedFootageProject(projectName) {
  return /caltrans/i.test(String(projectName ?? "").trim());
}

function projectTitleForCompletedFootage(projectRow) {
  const sub = readSubsegmentSummaryFromAcf(projectRow?.acf_groups_json);
  return (
    sub?.project_name ??
    projectRow?.name ??
    projectRow?.site_name ??
    projectRow?.customer ??
    ""
  );
}

export const FOOTAGE_METRIC_KEYS = new Set([
  "Total Feet",
  "Existing Feet",
  NEW_FEET_METRIC_KEY,
  "Awarded Footage",
  "Remaining Feet to Award",
  "Construction Footage",
  "Completed Footage",
  COMPLETED_CONDUIT_METRIC_KEY,
  COMPLETED_FIBER_METRIC_KEY,
  COMPLETED_PROOFING_METRIC_KEY,
  "Remaining Footage to Complete",
  TOTAL_PERMITTED_METRIC_KEY,
]);

/** Expansion reporting Metric dropdown — aligned with monthly expansion reporting. */
export const EXPANSION_BUDGET_METRICS = [
  { key: "Total Budget", label: "Total Budget" },
  { key: TOTAL_PERMITTED_METRIC_KEY, label: TOTAL_PERMITTED_METRIC_KEY },
  { key: "Awarded Footage", label: "Awarded Footage" },
  { key: COMPLETED_CONDUIT_METRIC_KEY, label: COMPLETED_CONDUIT_METRIC_KEY },
  { key: COMPLETED_FIBER_METRIC_KEY, label: COMPLETED_FIBER_METRIC_KEY },
  { key: BUDGETED_METRIC_KEY, label: BUDGETED_METRIC_KEY },
];

export const EXPANSION_BUDGET_GROUP_BY = [
  { key: "segment", label: "Segment" },
  { key: "owner", label: "Owner" },
  { key: "contractor", label: "Contractor" },
  { key: "phase", label: "Phase" },
  { key: "status", label: "Award Status" },
];

export const EXPANSION_BUDGET_CHART_TOP_N = 10;

/** Metrics whose comparison bars show completion % (value / target), not relative magnitude. */
export const PROGRESS_METRIC_KEYS = new Set([
  "Completed Footage",
  COMPLETED_CONDUIT_METRIC_KEY,
  COMPLETED_FIBER_METRIC_KEY,
  COMPLETED_PROOFING_METRIC_KEY,
]);

export function resolveReportMetricKey(metricKey) {
  if (metricKey === BUDGETED_METRIC_KEY) return "Awarded Budget Value";
  return metricKey;
}

export const EXPANSION_BUDGET_TIMEFRAMES = [
  { key: "all", label: "All time" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "365d", label: "Last 12 months" },
  { key: "custom", label: "Custom range" },
];

const CHART_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#0891b2",
  "#4f46e5",
  "#c026d3",
  "#0d9488",
  "#dc2626",
  "#65a30d",
];

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

function segmentCreatedAt(children) {
  let earliest = null;
  for (const row of children) {
    const d = parseDate(row?.created_at);
    if (!d) continue;
    if (!earliest || d < earliest) earliest = d;
  }
  return earliest;
}

function dominantPhase(children) {
  const counts = new Map();
  for (const row of children) {
    const phase = displayPhaseForRow(row);
    if (!phase) continue;
    counts.set(phase, (counts.get(phase) || 0) + 1);
  }
  let best = "";
  let bestCount = 0;
  for (const [phase, count] of counts) {
    if (count > bestCount) {
      best = phase;
      bestCount = count;
    }
  }
  return best;
}

export function timeframeBounds(timeframeKey, customFrom, customTo) {
  if (timeframeKey === "all") return { from: null, to: null };
  const now = new Date();
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  if (timeframeKey === "custom") {
    const from = customFrom ? parseDate(`${customFrom}T00:00:00`) : null;
    const customEnd = customTo ? parseDate(`${customTo}T23:59:59`) : null;
    return { from, to: customEnd };
  }
  const days = timeframeKey === "30d" ? 30 : timeframeKey === "90d" ? 90 : 365;
  const from = new Date(to);
  from.setDate(from.getDate() - days);
  from.setHours(0, 0, 0, 0);
  return { from, to };
}

function matchesTimeframe(createdAt, bounds) {
  if (!bounds.from && !bounds.to) return true;
  if (!createdAt) return false;
  if (bounds.from && createdAt < bounds.from) return false;
  if (bounds.to && createdAt > bounds.to) return false;
  return true;
}

function splitContractorLabels(raw) {
  if (!raw || !String(raw).trim()) return ["(No contractor)"];
  return String(raw)
    .split(/[,;|/]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function groupLabelsForRow(row, groupByKey) {
  switch (groupByKey) {
    case "owner":
      return [row.Owner?.trim() || "(No owner)"];
    case "contractor":
      return splitContractorLabels(row["Contractor(s)"]);
    case "phase":
      return [row.phase?.trim() || "(No phase)"];
    case "status":
      return [row.Status?.trim() || "(No status)"];
    case "segment":
    default: {
      const seg = row["Seg#"];
      if (seg != null && seg !== "") return [`Segment ${seg}`];
      return [row.woBase?.trim() || "(Unknown segment)"];
    }
  }
}

function metricValue(row, metricKey) {
  const resolved = resolveReportMetricKey(metricKey);
  const v = row[resolved] ?? row[metricKey];
  return v != null && Number.isFinite(Number(v)) ? Number(v) : 0;
}

function progressTargetForMetric(row, metricKey) {
  switch (metricKey) {
    case COMPLETED_CONDUIT_METRIC_KEY:
      return metricValue(row, "Construction Footage");
    case COMPLETED_PROOFING_METRIC_KEY:
      return metricValue(row, "Construction Footage");
    case COMPLETED_FIBER_METRIC_KEY:
    case "Completed Footage":
    default:
      return metricValue(row, "Total Feet");
  }
}

export function isProgressMetric(metricKey) {
  return PROGRESS_METRIC_KEYS.has(metricKey);
}

export function buildExpansionBudgetReportRows(projects, financialsMap, contractFieldsByProjectId) {
  const expansionRows = (Array.isArray(projects) ? projects : []).filter(isCvinExpansion);
  const groups = buildExpansionSegmentGroups(expansionRows);
  return groups.map((group) => {
    const stats = group.stats || {};
    const detail = buildExpansionSegmentDetailRow({
      woBase: group.woBase || group.key,
      segmentLabel: group.segment,
      children: group.children || [],
      financialsRaw: financialsMap?.[group.woBase || group.key] || financialsMap?.[group.key],
      contractFieldsByProjectId,
    });
    const totalFeet = detail["Total Feet"];
    const completedFootage = stats.completedFootageTotal;
    const remainingToComplete =
      totalFeet != null && completedFootage != null
        ? Math.max(0, totalFeet - completedFootage)
        : null;

    return {
      ...detail,
      "Construction Footage": stats.constructionFootageTotal,
      "Completed Footage": completedFootage,
      [COMPLETED_CONDUIT_METRIC_KEY]: stats.completedConduitFootageTotal,
      [COMPLETED_FIBER_METRIC_KEY]: stats.completedFiberFootageTotal,
      [COMPLETED_PROOFING_METRIC_KEY]: stats.completedProofingFootageTotal,
      "Remaining Footage to Complete": remainingToComplete,
      phase: dominantPhase(group.children || []),
      createdAt: segmentCreatedAt(group.children || []),
      subsegmentCount: (group.children || []).length,
      title: group.title,
      percentComplete: stats.percentComplete,
    };
  });
}

export function filterExpansionBudgetRows(rows, filters) {
  const {
    phases = [],
    timeframe = "all",
    customFrom = "",
    customTo = "",
  } = filters || {};
  const bounds = timeframeBounds(timeframe, customFrom, customTo);
  const phaseSet = new Set((phases || []).map((p) => String(p).trim()).filter(Boolean));

  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (phaseSet.size > 0 && !phaseSet.has(row.phase)) return false;
    return matchesTimeframe(row.createdAt, bounds);
  });
}

/**
 * Construction footage credited when a subsegment is first started (first inspector
 * units report). Credits the entire construction_footage once — not partial units.
 */
export function startedConstructionFootageForSubsegment(projectRow, bounds) {
  if (isExpansionParentShellRow(projectRow)) return null;
  const firstReportDate = firstInspectorUnitsReportDate(projectRow);
  if (!firstReportDate) return null;
  if (!matchesTimeframe(firstReportDate, bounds || { from: null, to: null })) return null;
  const sub = readSubsegmentSummaryFromAcf(projectRow?.acf_groups_json);
  const construction = parseFootageNumber(sub?.construction_footage);
  if (construction == null || construction <= 0) return null;
  return construction;
}

function childrenByWoBaseFromProjects(expansionProjects) {
  const groups = buildExpansionSegmentGroups(
    (Array.isArray(expansionProjects) ? expansionProjects : []).filter(isCvinExpansion)
  );
  const map = new Map();
  for (const group of groups) {
    const key = group.woBase || group.key;
    if (key) map.set(key, group.children || []);
  }
  return map;
}

/**
 * New Feet reporting: sum full construction footage for subsegments whose first
 * inspector units report falls in the timeframe. Does not use segment created_at.
 */
export function filterNewFeetStartedRows(reportRows, expansionProjects, filters) {
  const {
    phases = [],
    timeframe = "all",
    customFrom = "",
    customTo = "",
  } = filters || {};
  const bounds = timeframeBounds(timeframe, customFrom, customTo);
  const phaseSet = new Set((phases || []).map((p) => String(p).trim()).filter(Boolean));
  const childrenByWoBase = childrenByWoBaseFromProjects(expansionProjects);

  return (Array.isArray(reportRows) ? reportRows : [])
    .map((row) => {
      const children = childrenByWoBase.get(row.woBase) || [];
      let newFeet = 0;
      let hasNew = false;
      for (const child of children) {
        const footage = startedConstructionFootageForSubsegment(child, bounds);
        if (footage == null) continue;
        newFeet += footage;
        hasNew = true;
      }
      return {
        ...row,
        [NEW_FEET_METRIC_KEY]: hasNew ? Math.round(newFeet * 100) / 100 : null,
      };
    })
    .filter((row) => {
      if (phaseSet.size > 0 && !phaseSet.has(row.phase)) return false;
      return metricValue(row, NEW_FEET_METRIC_KEY) !== 0;
    });
}

/**
 * Completed Conduit / Fiber / Proofing reporting: sum inspector daily-progress footage whose
 * report date falls in the timeframe (not stored cumulative totals / created_at).
 * Also splits totals into Caltrans vs Non-Caltrans (by project name, same as permitted).
 */
export function filterInspectorCompletedFootageRows(reportRows, expansionProjects, filters) {
  const {
    phases = [],
    timeframe = "all",
    customFrom = "",
    customTo = "",
    metricKey = COMPLETED_CONDUIT_METRIC_KEY,
  } = filters || {};
  const bounds = timeframeBounds(timeframe, customFrom, customTo);
  const phaseSet = new Set((phases || []).map((p) => String(p).trim()).filter(Boolean));
  const childrenByWoBase = childrenByWoBaseFromProjects(expansionProjects);
  const activeMetric = isInspectorCompletedFootageMetric(metricKey)
    ? metricKey
    : COMPLETED_CONDUIT_METRIC_KEY;

  return (Array.isArray(reportRows) ? reportRows : [])
    .map((row) => {
      const children = childrenByWoBase.get(row.woBase) || [];
      let conduit = 0;
      let fiber = 0;
      let proofing = 0;
      let caltransConduit = 0;
      let nonCaltransConduit = 0;
      let caltransFiber = 0;
      let nonCaltransFiber = 0;
      let caltransProofing = 0;
      let nonCaltransProofing = 0;
      let hasConduit = false;
      let hasFiber = false;
      let hasProofing = false;
      for (const child of children) {
        if (isExpansionParentShellRow(child)) continue;
        const sums = sumInspectorReportedFootage(child, bounds);
        if (!sums.hasConduit && !sums.hasFiber && !sums.hasProofing) continue;
        const isCaltrans = isCaltransCompletedFootageProject(projectTitleForCompletedFootage(child));
        if (sums.hasConduit) {
          conduit += sums.conduitFootage;
          hasConduit = true;
          if (isCaltrans) caltransConduit += sums.conduitFootage;
          else nonCaltransConduit += sums.conduitFootage;
        }
        if (sums.hasFiber) {
          fiber += sums.fiberFootage;
          hasFiber = true;
          if (isCaltrans) caltransFiber += sums.fiberFootage;
          else nonCaltransFiber += sums.fiberFootage;
        }
        if (sums.hasProofing) {
          proofing += sums.proofingFootage;
          hasProofing = true;
          if (isCaltrans) caltransProofing += sums.proofingFootage;
          else nonCaltransProofing += sums.proofingFootage;
        }
      }
      return {
        ...row,
        [COMPLETED_CONDUIT_METRIC_KEY]: hasConduit ? Math.round(conduit * 100) / 100 : null,
        [COMPLETED_FIBER_METRIC_KEY]: hasFiber ? Math.round(fiber * 100) / 100 : null,
        [COMPLETED_PROOFING_METRIC_KEY]: hasProofing ? Math.round(proofing * 100) / 100 : null,
        caltransConduitFootage: hasConduit ? Math.round(caltransConduit * 100) / 100 : null,
        nonCaltransConduitFootage: hasConduit ? Math.round(nonCaltransConduit * 100) / 100 : null,
        caltransFiberFootage: hasFiber ? Math.round(caltransFiber * 100) / 100 : null,
        nonCaltransFiberFootage: hasFiber ? Math.round(nonCaltransFiber * 100) / 100 : null,
        caltransProofingFootage: hasProofing ? Math.round(caltransProofing * 100) / 100 : null,
        nonCaltransProofingFootage: hasProofing ? Math.round(nonCaltransProofing * 100) / 100 : null,
      };
    })
    .filter((row) => {
      if (phaseSet.size > 0 && !phaseSet.has(row.phase)) return false;
      return metricValue(row, activeMetric) !== 0;
    });
}

export function summarizeInspectorCompletedByCaltrans(rows, metricKey) {
  const activeMetric = isInspectorCompletedFootageMetric(metricKey)
    ? metricKey
    : COMPLETED_CONDUIT_METRIC_KEY;
  const isConduit = activeMetric === COMPLETED_CONDUIT_METRIC_KEY;
  const isFiber = activeMetric === COMPLETED_FIBER_METRIC_KEY;
  let caltransFootage = 0;
  let nonCaltransFootage = 0;
  let segmentsWithData = 0;

  for (const row of Array.isArray(rows) ? rows : []) {
    const value = metricValue(row, activeMetric);
    if (value === 0) continue;
    segmentsWithData += 1;
    if (isConduit) {
      caltransFootage += Number(row.caltransConduitFootage) || 0;
      nonCaltransFootage += Number(row.nonCaltransConduitFootage) || 0;
    } else if (isFiber) {
      caltransFootage += Number(row.caltransFiberFootage) || 0;
      nonCaltransFootage += Number(row.nonCaltransFiberFootage) || 0;
    } else {
      caltransFootage += Number(row.caltransProofingFootage) || 0;
      nonCaltransFootage += Number(row.nonCaltransProofingFootage) || 0;
    }
  }

  caltransFootage = Math.round(caltransFootage * 100) / 100;
  nonCaltransFootage = Math.round(nonCaltransFootage * 100) / 100;

  return {
    segmentCount: Array.isArray(rows) ? rows.length : 0,
    segmentsWithData,
    caltransFootage,
    nonCaltransFootage,
    total: Math.round((caltransFootage + nonCaltransFootage) * 100) / 100,
  };
}

export function aggregateInspectorCompletedCaltransPie(rows, metricKey) {
  const summary = summarizeInspectorCompletedByCaltrans(rows, metricKey);
  const total = summary.total;
  return [
    {
      name: "Caltrans",
      value: summary.caltransFootage,
      color: CALTRANS_PIE_COLORS.caltrans,
      pct: total > 0 ? summary.caltransFootage / total : 0,
      faded: false,
    },
    {
      name: "Non-Caltrans",
      value: summary.nonCaltransFootage,
      color: CALTRANS_PIE_COLORS.nonCaltrans,
      pct: total > 0 ? summary.nonCaltransFootage / total : 0,
      faded: false,
    },
  ].filter((slice) => slice.value > 0);
}

function progressBarPercent(value, target) {
  if (target == null || target <= 0) return undefined;
  return Math.min(100, (value / target) * 100);
}

export function aggregateExpansionBudgetChart(rows, { metricKey, groupByKey, topN = EXPANSION_BUDGET_CHART_TOP_N }) {
  const trackProgress = isProgressMetric(metricKey);
  const buckets = new Map();
  const targetBuckets = trackProgress ? new Map() : null;
  for (const row of rows) {
    const labels = groupLabelsForRow(row, groupByKey);
    const value = metricValue(row, metricKey);
    if (!labels.length || value === 0) continue;
    const share = value / labels.length;
    const targetShare = trackProgress ? progressTargetForMetric(row, metricKey) / labels.length : 0;
    for (const label of labels) {
      buckets.set(label, (buckets.get(label) || 0) + share);
      if (targetBuckets) {
        targetBuckets.set(label, (targetBuckets.get(label) || 0) + targetShare);
      }
    }
  }
  const entries = [...buckets.entries()]
    .map(([name, value]) => {
      const roundedValue = Math.round(value * 100) / 100;
      const target = targetBuckets
        ? Math.round((targetBuckets.get(name) || 0) * 100) / 100
        : null;
      return {
        name,
        value: roundedValue,
        target,
        barPercent: trackProgress ? progressBarPercent(roundedValue, target) : undefined,
      };
    })
    .filter((e) => e.value !== 0)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

  const total = entries.reduce((sum, e) => sum + e.value, 0);
  const colored = entries.map((entry, index) => ({
    ...entry,
    color: CHART_COLORS[index % CHART_COLORS.length],
    pct: total !== 0 ? entry.value / total : 0,
    faded: false,
  }));

  if (colored.length <= topN) return colored;

  const top = colored.slice(0, topN);
  const rest = colored.slice(topN);
  const otherValue = Math.round(rest.reduce((sum, e) => sum + e.value, 0) * 100) / 100;
  if (otherValue === 0) return top;

  const otherTarget = trackProgress
    ? Math.round(rest.reduce((sum, e) => sum + (e.target || 0), 0) * 100) / 100
    : null;

  return [
    ...top,
    {
      name: `Other (${rest.length})`,
      value: otherValue,
      target: otherTarget,
      barPercent: trackProgress ? progressBarPercent(otherValue, otherTarget) : undefined,
      color: "#cbd5e1",
      pct: total !== 0 ? otherValue / total : 0,
      faded: true,
    },
  ];
}

export function toExpansionBudgetGridRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    id: row.woBase || String(row["Seg#"] ?? ""),
    segNumber: row["Seg#"],
    woBase: row.woBase || "",
    owner: row.Owner || "",
    contractors: row["Contractor(s)"] || "",
    phase: row.phase || "",
    status: row.Status || "",
    totalFeet: row["Total Feet"],
    completedFootage: row["Completed Footage"],
    completedConduitFootage: row[COMPLETED_CONDUIT_METRIC_KEY],
    completedFiberFootage: row[COMPLETED_FIBER_METRIC_KEY],
    completedProofingFootage: row[COMPLETED_PROOFING_METRIC_KEY],
    caltransConduitFootage: row.caltransConduitFootage,
    nonCaltransConduitFootage: row.nonCaltransConduitFootage,
    caltransFiberFootage: row.caltransFiberFootage,
    nonCaltransFiberFootage: row.nonCaltransFiberFootage,
    caltransProofingFootage: row.caltransProofingFootage,
    nonCaltransProofingFootage: row.nonCaltransProofingFootage,
    awardedFootage: row["Awarded Footage"],
    remainingFeetToAward: row["Remaining Feet to Award"],
    remainingFootageToComplete: row["Remaining Footage to Complete"],
    totalBudget: row["Total Budget"],
    awardedBudget: row["Awarded Budget Value"] ?? row[BUDGETED_METRIC_KEY],
    budgeted: row[BUDGETED_METRIC_KEY] ?? row["Awarded Budget Value"],
    remainingBudget: row["Remaining Budget to Award"],
    expectedFinalCost: row["Expected Final Cost"],
    variance: row["Final Variance vs Budget"],
  }));
}

export function summarizeExpansionBudget(rows, metricKey) {
  const values = rows.map((row) => metricValue(row, metricKey));
  const total = values.reduce((sum, v) => sum + v, 0);
  const withBudget = rows.filter((row) => metricValue(row, metricKey) !== 0).length;
  return {
    segmentCount: rows.length,
    segmentsWithData: withBudget,
    total: Math.round(total * 100) / 100,
  };
}

/** Sum of segment Total Feet, optionally filtered by dominant phase (not timeframe). */
export function sumExpansionProjectFootage(segmentRows, { phases = [] } = {}) {
  const phaseSet = new Set((phases || []).map((p) => String(p).trim()).filter(Boolean));
  let total = 0;
  for (const row of Array.isArray(segmentRows) ? segmentRows : []) {
    if (phaseSet.size > 0 && !phaseSet.has(row.phase)) continue;
    total += metricValue(row, "Total Feet");
  }
  return Math.round(total * 100) / 100;
}

export function permittedFootageProjectPercent(permittedFootage, totalProjectFootage) {
  if (totalProjectFootage == null || totalProjectFootage <= 0) return null;
  const permitted = permittedFootage != null && Number.isFinite(Number(permittedFootage)) ? Number(permittedFootage) : 0;
  return Math.round((permitted / totalProjectFootage) * 1000) / 10;
}

export function formatBudgetCurrency(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return Number(value).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export function isFootageMetric(metricKey) {
  return FOOTAGE_METRIC_KEYS.has(metricKey);
}

export function isTotalPermittedMetric(metricKey) {
  return metricKey === TOTAL_PERMITTED_METRIC_KEY;
}

export function isNewFeetMetric(metricKey) {
  return metricKey === NEW_FEET_METRIC_KEY;
}

export function isInspectorCompletedFootageMetric(metricKey) {
  return (
    metricKey === COMPLETED_CONDUIT_METRIC_KEY ||
    metricKey === COMPLETED_FIBER_METRIC_KEY ||
    metricKey === COMPLETED_PROOFING_METRIC_KEY
  );
}

export function formatReportFootage(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return formatExpansionFootage(Number(value));
}

export function formatReportPercent(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const rounded = Math.round(Number(value) * 10) / 10;
  return `${rounded}%`;
}

export function formatReportMetric(value, metricKey) {
  if (isFootageMetric(metricKey)) return formatReportFootage(value);
  return formatBudgetCurrency(value);
}

export { GLOBAL_PHASES, EXPANSION_PHASE_FLOW };
